import React, { useEffect } from "react";

import { changeRAndDUpperCase, IconExternalLink } from "@frontend/common";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { SimilarPressData } from "@api/dealsSimilarData/dealsSimilarData.type";
import { formatDate } from "@utils/formatDate";

import * as S from "./DealSimilarityPressTableRow.style";
import { Checkbox } from "../OverlappedCheckBox/OverlappedCheckBox";
import { getSimilarityClassificationDisplayName } from "../SimilarPressTableContainer/utils/getSimilarityClassificationDisplayName";
import { isWithinFourDays } from "../SimilarPressTableContainer/utils/isWithinFourDays";
import { moveToFillInTable } from "../SimilarPressTableContainer/utils/moveToFillInTable";

type Props = {
  targetPressId: number;
  similarPressItem: SimilarPressData;
  dealTargetPressDate: string;
  targetCategoryType: "PRESS" | "FILING";
  similarCategoryType: "PRESS" | "FILING";
  hoveredRow: number | null;
  setHoveredRow: (hoveredRow: number | null) => void;
  handleCheckboxChange: ({
    currentSimilarPressId,
    currentSimilarCategoryType,
    currentTargetCategoryType,
    similarityPressDuplId,
    similarityPressRejectReason,
  }: {
    currentSimilarPressId: number;
    currentSimilarCategoryType: "PRESS" | "FILING";
    currentTargetCategoryType: "PRESS" | "FILING";
    similarityPressDuplId: number;
    similarityPressRejectReason: string;
  }) => Promise<void>;
  overlappedId: number | undefined;
  changeCheckedCheckbox: (isChecked: boolean) => void;
  targetPressDuplId: number;
};

const DealSimilarityPressTableRow = ({
  similarPressItem,
  dealTargetPressDate,
  targetCategoryType,
  similarCategoryType,
  hoveredRow,
  setHoveredRow,
  handleCheckboxChange,
  overlappedId,
  targetPressId,
  changeCheckedCheckbox,
  targetPressDuplId,
}: Props) => {
  const isDifferentCategoryType = similarCategoryType !== targetCategoryType;
  const { data: articleInfo } = useArticleInfoQuery(similarPressItem.pressId);

  useEffect(() => {
    changeCheckedCheckbox(
      isDifferentCategoryType
        ? !!articleInfo?.duplArticleId
        : !!targetPressDuplId,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleInfo, isDifferentCategoryType, targetPressDuplId]);

  return (
    <S.SimilarPressTableBody
      key={similarPressItem.pressId}
      onMouseEnter={() => setHoveredRow(similarPressItem.pressId)}
      onMouseLeave={() => setHoveredRow(null)}
      hovered={hoveredRow === similarPressItem.pressId}
    >
      <S.SimilarPressTablePressIdWrapper>
        <S.SimilarPressId
          onClick={() => moveToFillInTable(`${similarPressItem.pressId}`)}
        >
          #{similarPressItem.pressId}
        </S.SimilarPressId>
        <S.SimilarPressIcon
          onClick={() => moveToFillInTable(`${similarPressItem.pressId}`)}
        >
          <IconExternalLink width={20} height={20} />
        </S.SimilarPressIcon>
      </S.SimilarPressTablePressIdWrapper>

      <S.SimilarPressTableScore>
        <span>{similarPressItem.score.toFixed(4)}</span>
      </S.SimilarPressTableScore>

      <S.SimilarPressTablePressDateData>
        <div>
          <S.SimilarPressCheckDate
            isWithinFourDays={isWithinFourDays(
              similarPressItem.pressDate[0]?.text,
              dealTargetPressDate,
            )}
          >
            {formatDate(similarPressItem.pressDate[0]?.text, "YYYY.MM.DD")}
          </S.SimilarPressCheckDate>
          {similarPressItem.pressDate.map((pressDate) => (
            <S.SimilarPressCheckDate
              isWithinFourDays={isWithinFourDays(
                similarPressItem.pressDate[0]?.text,
                dealTargetPressDate,
              )}
            >
              {pressDate.isManual ? "(Manual)" : ""}
            </S.SimilarPressCheckDate>
          ))}
        </div>
      </S.SimilarPressTablePressDateData>

      <S.SimilarPressTableSellerData>
        <S.SimilarPressTableBodyDiv>
          {similarPressItem.seller.map((seller) => (
            <S.PressMatchedWrapper matched={seller.matched}>
              {seller.text}
            </S.PressMatchedWrapper>
          ))}
        </S.SimilarPressTableBodyDiv>
      </S.SimilarPressTableSellerData>

      <S.SimilarPressTableBuyerData>
        <S.SimilarPressTableBodyDiv>
          {similarPressItem.buyer.map((buyer) => (
            <S.PressMatchedWrapper matched={buyer.matched}>
              {buyer.text}
            </S.PressMatchedWrapper>
          ))}
        </S.SimilarPressTableBodyDiv>
      </S.SimilarPressTableBuyerData>

      <S.SimilarPressTableClassificationData>
        <S.SimilarPressTableBodyDiv>
          {getSimilarityClassificationDisplayName(
            similarPressItem.classification,
          )}
        </S.SimilarPressTableBodyDiv>
      </S.SimilarPressTableClassificationData>

      <S.SimilarPressTableDealTypeData>
        <S.SimilarPressTableBodyDiv>
          {similarPressItem.dealType.map((dealType) => (
            <S.PressMatchedWrapper matched={dealType.matched}>
              {changeRAndDUpperCase(dealType.text)}
            </S.PressMatchedWrapper>
          ))}
        </S.SimilarPressTableBodyDiv>
      </S.SimilarPressTableDealTypeData>

      <S.SimilarPressTablePressTitleData>
        <S.SimilarPressTablePressTitleOverflow>
          {similarPressItem.pressTitle}
        </S.SimilarPressTablePressTitleOverflow>
      </S.SimilarPressTablePressTitleData>
      <S.SimilarPressTableUnsuitableData>
        <div>
          <div>Overlapped</div>
          <S.CheckBoxContainer>
            <Checkbox
              onChangeHandler={() =>
                handleCheckboxChange({
                  currentSimilarPressId: similarPressItem.pressId,
                  currentSimilarCategoryType: similarCategoryType,
                  currentTargetCategoryType: targetCategoryType,
                  similarityPressDuplId: articleInfo?.duplArticleId,
                  similarityPressRejectReason:
                    articleInfo?.articleRejectReason || "",
                })
              }
              value={
                isDifferentCategoryType
                  ? articleInfo.draftArticleId
                  : similarPressItem.pressId
              }
              checked={
                isDifferentCategoryType
                  ? similarCategoryType === "PRESS"
                    ? similarPressItem.pressId === overlappedId
                    : articleInfo.duplArticleId === targetPressId
                  : similarPressItem.pressId === overlappedId
              }
            />
          </S.CheckBoxContainer>
        </div>
      </S.SimilarPressTableUnsuitableData>
    </S.SimilarPressTableBody>
  );
};

export default DealSimilarityPressTableRow;
